import { Bell, ChevronDown, Settings, User, Moon, Sun, Palette, Languages } from 'lucide-react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from '@/components/ui/dropdown-menu';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './NavigationBar.css';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AutoBreadcrumb, useBreadcrumbs } from '@/lib/breadcrumb';
import { useTenancy } from '@/contexts/TenancyContext';
import { useTheme, type ThemeMode } from '../contexts/ThemeContext';
import { useUser } from '@/contexts/UserContextProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faBuildings, faMoonStars, faSunBright, faPalette, faDoorOpen, faUser, faChevronDown, faGlobe } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { NotificationCenter } from './NotificationCenter';
import DashboardRefreshButton from '@/app/pages/dashboard/DashboardRefreshButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import { toast } from 'sonner';
import { saveUserInfo } from '@/services/userService';
import { isFeatureEnabled } from '@/utils/featuresUtil';

interface BreadcrumbItem {
  label: string;
  path?: string;
  active?: boolean;
}

interface NavigationBarProps {
  tenancyName?: string;
  breadcrumbs?: Array<BreadcrumbItem>;
  onToggleSidebar?: () => void;
  userImage?: string;
}

const NavigationBar: React.FC<NavigationBarProps> = ({ tenancyName = '', breadcrumbs = [], onToggleSidebar, userImage = '' }) => {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [currentLocale, setCurrentLocale] = useState('en-au');
  const { activeTenancy } = useTenancy();
  const { user: userOption } = useUser();
  const user = Array.isArray(userOption) ? userOption[0] : userOption;
  const { mode, setMode, isDark } = useTheme();
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  
  // Check if Language Picker feature is enabled
  const isLanguagePickerEnabled = isFeatureEnabled('Language Picker');

  // Language options
  const languages = [
    { code: 'en-au', name: 'English (AU)', flag: '🇦🇺' },
    { code: 'en-us', name: 'English (US)', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  ];

  // Initialize current locale from i18n API
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).deiterateI18n) {
      const locale = (window as any).deiterateI18n.getLocale();
      setCurrentLocale(locale || 'en-au');
    }
  }, []);

  // Handle language change
  const handleLanguageChange = (localeCode: string) => {
    if (typeof window !== 'undefined' && (window as any).deiterateI18n) {
      (window as any).deiterateI18n.setLanguage(localeCode);
      setCurrentLocale(localeCode);
      toast.success(`Language changed to ${languages.find(l => l.code === localeCode)?.name}`);
    }
  };

  // Get automatic breadcrumbs
  const autoBreadcrumbs = useBreadcrumbs();

  // Use provided breadcrumbs or automatic ones
  const displayBreadcrumbs = breadcrumbs.length > 0 ? breadcrumbs : autoBreadcrumbs;

  // Save theme preferences (same method as ThemeSettings.tsx)
  const saveThemePreference = useCallback(async (updates: {
    mode?: ThemeMode;
  }) => {
    if (!user) {
      console.error('No user found, cannot save theme preferences');
      toast.error('You must be logged in to save theme preferences');
      return false;
    }

    const toastId = toast.loading('Saving theme preferences...');
    console.log('Saving theme preferences:', updates);

    try {
      const themeUpdate = {
        ...(user.theme || {}),
        ...updates
      };

      console.log('Theme update payload:', { theme: themeUpdate });

      // await saveUserInfo({
      //   theme: themeUpdate
      // });

      console.log('Theme preferences saved successfully');
      toast.success('Theme preferences saved', { id: toastId });
      return true;
    } catch (error) {
      console.error('Failed to save theme preferences:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save theme preferences';
      toast.error(errorMessage, { id: toastId });
      return false;
    }
  }, [user]);

  // Toggle between light and dark mode (same method as ThemeSettings.tsx)
  const toggleTheme = async () => {
    const newMode = isDark ? 'light' : 'dark';
    console.log('NavigationBar.toggleTheme: changing mode from', mode, 'to', newMode, {
      currentMode: mode,
      isDark,
      newMode,
      stackTrace: new Error().stack?.split('\n').slice(1, 5).join('\n')
    });
    setMode(newMode);
    const success = await saveThemePreference({ mode: newMode });
    if (!success) {
      // Revert UI if save fails
      const savedMode = user?.theme?.mode || 'system';
      console.log('NavigationBar.toggleTheme: reverting to saved mode', savedMode);
      setMode(savedMode as ThemeMode);
    }
  };

  return (
    <div className="navigation-bar border-b w-full">
      <div className="flex flex-col w-full h-auto">
        <div className="flex h-16 items-center px-4 w-full">
          <div className="flex items-center gap-4">
            {useIsMobile() && <SidebarTrigger />}
            <Link to="/" className="font-semibold text-lg">
              {activeTenancy?.name || tenancyName || 'Acme Inc'}
            </Link>

            {/* Integrated breadcrumbs */}
            {/* {displayBreadcrumbs.length > 1 && (
              <div className="hidden md:flex breadcrumb-container ml-4">
                <AutoBreadcrumb />
              </div>
            )} */}
          </div>
          <div className="ml-auto flex items-center gap-4">
            {/* Theme settings button */}
            {/* <button 
              onClick={() => navigate('/settings')} 
              className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-accent"
              aria-label="Theme settings"
            >
              <Palette className="h-5 w-5" />
            </button> */}
            <DashboardRefreshButton />
            <span id="notification-center-anchor" data-testid="notification-center-anchor">
              <NotificationCenter />
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border p-1 hover:bg-accent">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full ${user?.photoURL ? 'bg-primary/20' : 'bg-primary'} text-primary-foreground`}>
                    {user?.photoURL ? (
                      <img src={user?.photoURL} alt="User" className="h-8 w-8 rounded-full" />
                    ) : (
                      <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                    )}
                  </div>
                  <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* User Profile Section */}
                <div className="flex items-center gap-3 px-2 py-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full ${user?.photoURL ? 'bg-primary/20' : 'bg-primary'} text-primary-foreground flex-shrink-0`}>
                    {user?.photoURL ? (
                      <img src={user?.photoURL} alt="User" className="h-10 w-10 rounded-full" />
                    ) : (
                      <FontAwesomeIcon icon={faUser} className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex flex-col overflow-hidden">
                    <span className="font-semibold text-sm truncate">
                      {user?.name || 'User'}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {user?.email || 'No email'}
                    </span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {/* <DropdownMenuItem>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem> */}
                <DropdownMenuItem onClick={() => window.location.pathname.includes('.html') ? window.location.href = '/settings' : navigate('/settings')}>
                  <FontAwesomeIcon icon={faGear} className="mr-2 h-4 w-4" />
                  <span>User Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.location.pathname.includes('.html') ? window.location.href = '/msp-view' : navigate('/msp-view')}>
                  <FontAwesomeIcon icon={faBuildings} className="mr-2 h-4 w-4" />
                  <span>Manage Tenancies</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme}>
                  {isDark ? (
                    <FontAwesomeIcon icon={faSunBright} className="mr-2 h-4 w-4" />
                  ) : (
                    <FontAwesomeIcon icon={faMoonStars} className="mr-2 h-4 w-4" />
                  )}
                  <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.location.pathname.includes('.html') ? window.location.href = '/settings/theme' : navigate('/settings/theme')}>
                  <FontAwesomeIcon icon={faPalette} className="mr-2 h-4 w-4" />
                  <span>Colour Palette</span>
                </DropdownMenuItem>
                {isLanguagePickerEnabled && (
                  <>
                    <DropdownMenuSeparator />
                    {/* Language Picker Section */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <FontAwesomeIcon icon={faGlobe} className="mr-2 h-4 w-4" />
                        <span>Language</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {languages.find(l => l.code === currentLocale)?.flag}
                        </span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {languages.map((language) => (
                          <DropdownMenuItem
                            key={language.code}
                            onClick={() => handleLanguageChange(language.code)}
                            className={currentLocale === language.code ? 'bg-accent' : ''}
                          >
                            <span className="mr-2 text-lg">{language.flag}</span>
                            <span className="flex-1">{language.name}</span>
                            {currentLocale === language.code && (
                              <span className="text-xs text-primary">✓</span>
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.location.pathname.includes('.html') ? window.location.href = '/logout' : navigate('/logout')}>
                  <FontAwesomeIcon icon={faDoorOpen} className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile breadcrumbs - only shown on small screens */}
        {/* {displayBreadcrumbs.length > 1 && (
          <div className="mobile-breadcrumbs md:hidden sm:block dark:bg-secondary">
            <AutoBreadcrumb />
          </div>
        )} */}
      </div>
    </div>
  );
};

export default NavigationBar;
